import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { FormProvider, useForm, Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { TextInput } from "src/app/@core/forms/controls/textInput";
import { TextArea } from "src/app/@core/forms/controls/textArea";
import { CheckBox } from "src/app/@core/forms/controls/checkbox";
import { DialogHeaderContent } from "src/app/@core/forms/controls/dialogHeaderContent";
import {
  DialogBase,
  DialogModelBase,
} from "src/app/@core/forms/dialog/@dialogBaseV2";
// import { CheckBox } from "src/app/@core/forms/controls/checkbox";
import { isUoN } from "src/app/@core/common";

/** Model */
export class UserAddDialogModel extends DialogModelBase {
  constructor() {
    super();
    this.frmLocal = useForm({
      mode: "onChange",
      defaultValues: {
        firstName: "",
        lastName: "",
        middleName: "",
        title: "",
        email: "",
        retailer: null,
        contactNotes: "",
        allowLogin: false,
        password: "",
        confirmPassword: "",
      },
    });
    this.retailers = [];
  }
}

/** Controller */
export class UserAddDialogController extends DialogBase {
  constructor(model, setModel) {
    super(model, setModel);
  }

  /**
   *
   * @param {*} retailers
   * @returns
   */
  show = async (retailers) => {
    this.model.retailers = retailers;
    this.model.frmLocal.reset({
      firstName: "",
      lastName: "",
      middleName: "",
      title: "",
      email: "",
      retailer: null,
      contactNotes: "",
      allowLogin: false,
      password: "",
      confirmPassword: "",
    });
    return super.show();
  };

  /**
   *
   */
  submit = async () => {
    try {
      this.spinner().show();
      const obj = this.model.frmLocal.getValues();
      if (!isUoN(obj.retailer)) obj.retailerID = obj.retailer.id;
      obj.isActive = true;
      obj.dateCreated = new Date();
      await this.api().contactApi.insertEndUser(obj);
      this.hide(true);
    } catch (err) {
      this.hide(false);
    } finally {
      this.spinner().hide();
    }
  };
}

/** Component */
export const UserAdd = ({ controller }) => {
  const { frmLocal, retailers } = controller.model;
  const { control, handleSubmit } = frmLocal;
  const allowLogin = frmLocal.watch("allowLogin");

  /**
   *
   * @returns
   */
  const onSubmit = handleSubmit((data) => controller.submit(data));

  return (
    <Dialog
      fullScreen
      style={{ margin: "auto", maxWidth: 1000, maxHeight: 800 }}
      open={controller.visible()}
      onClose={controller.close}
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      aria-modal="true"
    >
      <DialogTitle>
        <DialogHeaderContent title="Add New User" onClose={controller.close} />
      </DialogTitle>

      <DialogContent dividers>
        <FormProvider {...frmLocal}>
          <div>
            <div className="flex -mx-4">
              <TextInput
                id="firstName"
                label="First Name"
                control={control}
                rules={{ required: "First name is required" }}
              />
              <TextInput
                id="lastName"
                label="Last Name"
                control={control}
                rules={{ required: "Last name is required" }}
              />
              <TextInput
                id="middleName"
                label="Middle Name"
                control={control}
              />
              <TextInput id="title" label="Title" control={control} />
            </div>

            <div className="flex">
              <div style={{ width: "50%", marginLeft: 5, marginRight: 10 }}>
                <Controller
                  name="retailer"
                  control={control}
                  rules={{ required: "Retailer is required" }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      disablePortal
                      autoComplete
                      id="retailer"
                      className="mt-8"
                      fullWidth
                      options={retailers}
                      value={field.value}
                      isOptionEqualToValue={(o, v) => o.id === v?.id}
                      onChange={(e, data) => field.onChange(data)}
                      getOptionLabel={(x) => x.name}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Retailer"
                          size="small"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </div>

              <div style={{ width: "50%", marginRight: 10 }}>
                <TextInput
                  id="email"
                  label="Email"
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email format",
                    },
                  }}
                />
              </div>
            </div>

            <div className="flex -mx-4">
              <TextArea id="contactNotes" label="Comments" control={control} />
            </div>

            <hr style={{ marginTop: 20, marginBottom: 10 }} />

            <h3
              style={{ marginBottom: 10, fontWeight: 600, color: "#7d7d7dff" }}
            >
              Login Information
            </h3>
            <div className="flex -mx-4">
              <div style={{ width: 400, paddingTop: 15 }}>
                <CheckBox
                  id="allowLogin"
                  label="Allow Login"
                  control={control}
                />
              </div>
              {allowLogin && (
                <>
                  <TextInput
                    id="password"
                    label="Password"
                    control={control}
                    type="password"
                    showPasswordToggle={true}
                    rules={{
                      required: allowLogin ? "Password is required" : false,
                    }}
                  />
                  <TextInput
                    id="confirmPassword"
                    label="Confirm Password"
                    control={control}
                    type="password"
                    showPasswordToggle={true}
                    rules={{
                      required: allowLogin
                        ? "Confirm password is required"
                        : false,
                      validate: (value) =>
                        value === frmLocal.getValues("password") ||
                        "Passwords do not match",
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="primary" onClick={controller.close}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" onClick={onSubmit}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
